/**
 * Which peers the development servers treat as this local network (ADR-050's amendment of 2026-09-28). The dev
 * server and a development gateway admit a peer only when its address falls in the allowed networks, and a
 * request's `Host` or `Origin` only when it names the machine by `localhost`, by its own host name or by a
 * literal address in those networks. Pure string and integer work, so both Bun servers share one rule.
 */

/**
 * The networks admitted when `ALLOWED_NETWORKS` is unset or empty: loopback, `10.0.0.0/8`, `192.168.0.0/16`,
 * link-local and IPv6 unique local addresses, none of which a router forwards from outside. The RFC 1918 range
 * `172.16.0.0/12` is left out: Docker's bridges live there, and a tunnel connector on a bridge (the workstation's
 * `cloudflared-ve`) brings internet traffic to the servers from a bridge address.
 */
export const DEFAULT_ALLOWED_NETWORKS = "127.0.0.0/8,::1/128,10.0.0.0/8,192.168.0.0/16,169.254.0.0/16,fe80::/10,fc00::/7";

/**
 * Loopback, admitted whatever `ALLOWED_NETWORKS` says so the machine's own tools and port forwards keep working.
 */
const LOOPBACK_NETWORKS = "127.0.0.0/8,::1/128";

const IPV4_PART = /^(0|[1-9]\d{0,2})$/;
const HEXTET = /^[0-9a-fA-F]{1,4}$/;
const PREFIX = /^\d{1,3}$/;
const HOST_HEADER = /^(?:\[([0-9a-fA-F:.]+)\]|([A-Za-z0-9.-]+))(?::(\d{1,5}))?$/;
const MAPPED_PREFIX_BITS = 96;
const GROUPS = 8;

/**
 * An address as its eight 16-bit groups, an IPv4 address held in its IPv4-mapped IPv6 form.
 */
type Groups = readonly number[];

/**
 * One allowed range: its first address's groups and its prefix length in the 128-bit space.
 */
interface Network {
  readonly base: Groups;
  readonly bits: number;
}

/**
 * The allowed networks, and each range written with its prefix length for the log.
 */
export interface AllowedNetworks {
  readonly includes: (address: string) => boolean;
  readonly entries: readonly string[];
}

/**
 * Parses the allowed networks.
 *
 * @param spec `ALLOWED_NETWORKS`: comma-separated addresses and CIDR ranges, IPv4 or IPv6. Unset or empty (after
 *   trimming) is `DEFAULT_ALLOWED_NETWORKS`. Loopback is admitted whatever it names.
 * @param variable The variable's name, for the error.
 * @returns The set. An IPv4 range also admits the IPv4-mapped IPv6 form of its addresses.
 * @throws Error naming the variable and the entry when one is neither an address nor a CIDR range.
 * @example
 * parseAllowedNetworks("192.168.10.0/24").includes("::ffff:192.168.10.5"); // true
 */
export function parseAllowedNetworks(spec: string | undefined, variable = "ALLOWED_NETWORKS"): AllowedNetworks {
  const chosen = spec === undefined || spec.trim() === "" ? DEFAULT_ALLOWED_NETWORKS : spec;
  const networks: Network[] = [];
  const entries: string[] = [];
  for (const entry of `${LOOPBACK_NETWORKS},${chosen}`.split(",")) {
    const trimmed = entry.trim();
    if (trimmed === "") {
      continue;
    }
    const parsed = parseNetwork(trimmed);
    if (parsed === null) {
      throw new Error(`${variable} entry "${trimmed}" is not an address or a CIDR range`);
    }
    if (!entries.includes(parsed.label)) {
      networks.push(parsed.network);
      entries.push(parsed.label);
    }
  }
  return {
    includes: (address) => {
      const groups = parseAddress(address);
      return groups !== null && networks.some((network) => inNetwork(groups, network));
    },
    entries,
  };
}

/**
 * Whether an address is this machine's loopback.
 *
 * @param address A peer address as the transport reports it.
 * @returns True for `127.0.0.0/8`, `::1` and the IPv4-mapped form of the first.
 */
export function isLoopbackAddress(address: string): boolean {
  return LOOPBACK.includes(address);
}

/**
 * The host name a `Host` header (or an origin's host and port) names.
 *
 * @param host The header's value, for example `192.168.10.110:3000` or `[::1]:3000`.
 * @returns The name lower-cased, without its port or an IPv6 literal's brackets, or null when the value is not
 *   one host with an optional port.
 */
export function hostHeaderName(host: string): string | null {
  const match = HOST_HEADER.exec(host);
  if (match === null || (match[3] !== undefined && Number(match[3]) > 65535)) {
    return null;
  }
  return (match[1] ?? match[2] ?? "").toLowerCase();
}

/**
 * Whether a host name names this machine as the local network reaches it: `localhost`, a literal address in the
 * allowed networks, or the machine's own host name, bare or with `.local` as multicast DNS gives it.
 *
 * @param name A host name as `hostHeaderName` gives it.
 * @param networks The allowed networks.
 * @param machine The machine's own host name (`os.hostname()`); empty for none.
 * @returns True when the name is admitted.
 */
export function isLocalName(name: string, networks: AllowedNetworks, machine: string): boolean {
  return isMachineName(name, machine) || networks.includes(name.toLowerCase());
}

/**
 * Whether a host name names this machine by name rather than by address: `localhost`, or the machine's own host
 * name, bare or with `.local` as multicast DNS gives it. The dev server's gate and a development gateway's list of
 * admitted origins both start from this rule and differ only in which literal addresses they add to it.
 *
 * @param name A host name as `hostHeaderName` gives it.
 * @param machine The machine's own host name (`os.hostname()`); empty for none, which admits `localhost` alone.
 * @returns True when the name is one of them, ignoring case.
 */
export function isMachineName(name: string, machine: string): boolean {
  const lower = name.toLowerCase();
  const own = machine.toLowerCase();
  return lower === "localhost" || (own !== "" && (lower === own || lower === `${own}.local`));
}

/**
 * One spelling for every way of writing an address, so two can be compared as text: its eight 16-bit groups in
 * lower-case hex, an IPv4 address written as its IPv4-mapped IPv6 form and a zone index dropped. So
 * `192.168.10.110`, `::ffff:192.168.10.110` and `::ffff:c0a8:a6e` are one address, and `::1` and
 * `0:0:0:0:0:0:0:1` are another.
 *
 * @param text An address as a peer, an interface or a host name gives it, without brackets.
 * @returns The canonical text, or null when it is not an address.
 * @example
 * canonicalAddress("::ffff:192.168.10.110") === canonicalAddress("192.168.10.110"); // true
 */
export function canonicalAddress(text: string): string | null {
  const groups = parseAddress(text);
  return groups === null ? null : groups.map((group) => group.toString(16)).join(":");
}

const LOOPBACK = parseAllowedNetworks(LOOPBACK_NETWORKS);

/**
 * Parses one entry of the list.
 *
 * @param entry The entry, trimmed.
 * @returns The range and its label, or null when it is malformed.
 */
function parseNetwork(entry: string): { readonly network: Network; readonly label: string } | null {
  const slash = entry.indexOf("/");
  const text = slash < 0 ? entry : entry.slice(0, slash);
  const v4 = parseIpv4(text);
  const groups = v4 ?? parseIpv6(text);
  if (groups === null) {
    return null;
  }
  const width = v4 === null ? 128 : 32;
  const prefixText = slash < 0 ? String(width) : entry.slice(slash + 1);
  if (!PREFIX.test(prefixText) || Number(prefixText) > width) {
    return null;
  }
  const prefix = Number(prefixText);
  const bits = v4 === null ? prefix : prefix + MAPPED_PREFIX_BITS;
  return { network: { base: groups, bits }, label: `${text.toLowerCase()}/${prefix}` };
}

/**
 * Whether an address falls in a range.
 *
 * @param groups The address.
 * @param network The range.
 * @returns True when the first `network.bits` bits of both agree.
 */
function inNetwork(groups: Groups, network: Network): boolean {
  let left = network.bits;
  for (let index = 0; index < GROUPS && left > 0; index += 1) {
    const take = Math.min(16, left);
    const mask = (0xffff << (16 - take)) & 0xffff;
    if (((groups[index] ?? 0) & mask) !== ((network.base[index] ?? 0) & mask)) {
      return false;
    }
    left -= take;
  }
  return true;
}

/**
 * Parses a peer address, IPv4 or IPv6 (a zone index such as `%enp3s0` dropped).
 *
 * @param address The text.
 * @returns The groups, or null when it is not an address.
 */
function parseAddress(address: string): Groups | null {
  const zone = address.indexOf("%");
  if (zone === 0 || zone === address.length - 1) {
    return null;
  }
  const bare = zone < 0 ? address : address.slice(0, zone);
  return parseIpv4(bare) ?? parseIpv6(bare);
}

/**
 * Parses a dotted-quad IPv4 address, with no leading zeros, into its IPv4-mapped IPv6 groups.
 *
 * @param text The text.
 * @returns The groups, or null.
 */
function parseIpv4(text: string): Groups | null {
  const octets = dottedQuad(text);
  if (octets === null) {
    return null;
  }
  return [0, 0, 0, 0, 0, 0xffff, (octets[0] << 8) | octets[1], (octets[2] << 8) | octets[3]];
}

/**
 * The four octets of a dotted quad.
 *
 * @param text The text.
 * @returns The octets, or null when it is not four decimal parts of 0 to 255 without leading zeros.
 */
function dottedQuad(text: string): [number, number, number, number] | null {
  const parts = text.split(".");
  if (parts.length !== 4 || !parts.every((part) => IPV4_PART.test(part) && Number(part) <= 255)) {
    return null;
  }
  const [a, b, c, d] = parts.map(Number) as [number, number, number, number];
  return [a, b, c, d];
}

/**
 * Parses an IPv6 address, `::` and a trailing dotted quad included.
 *
 * @param text The text.
 * @returns The eight groups, or null.
 */
function parseIpv6(text: string): Groups | null {
  if (!text.includes(":")) {
    return null;
  }
  const halves = text.split("::");
  if (halves.length > 2) {
    return null;
  }
  const front = sideGroups(halves[0] ?? "", halves.length === 1);
  const back = halves.length === 2 ? sideGroups(halves[1] ?? "", true) : [];
  if (front === null || back === null) {
    return null;
  }
  if (halves.length === 1) {
    return front.length === GROUPS ? front : null;
  }
  const zeros = GROUPS - front.length - back.length;
  return zeros >= 1 ? [...front, ...new Array<number>(zeros).fill(0), ...back] : null;
}

/**
 * The groups on one side of `::` (or of a whole address without one).
 *
 * @param side The text.
 * @param last Whether this side ends the address, where a dotted quad may stand for the last two groups.
 * @returns The groups, or null when a part is malformed.
 */
function sideGroups(side: string, last: boolean): number[] | null {
  if (side === "") {
    return [];
  }
  const parts = side.split(":");
  const groups: number[] = [];
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index] ?? "";
    if (last && index === parts.length - 1 && part.includes(".")) {
      const octets = dottedQuad(part);
      if (octets === null) {
        return null;
      }
      groups.push((octets[0] << 8) | octets[1], (octets[2] << 8) | octets[3]);
    } else if (HEXTET.test(part)) {
      groups.push(Number.parseInt(part, 16));
    } else {
      return null;
    }
  }
  return groups;
}
